<template>
<div class="chapter">
<div class="inner_page">
<h2 class="h2a"><a id="page_20">Chapter 2</a></h2><a id="page_20">
<p class="imagec"><img alt="images" src="images/common.jpg"/></p>
<h2 class="h2b">Are You Sugar Sensitive?</h2>
<p class="noindent"><span class="stickup">B</span>y now you are probably wondering if you, too, are sugar sensitive. And how sugar sensitive are you?</p>
<p class="indentb">There are two ways to determine this, both of which I use with my clients. Some people prefer the informal approach; others like using the checklist later in this chapter. Let’s start with an informal way to diagnose sugar sensitivity. When a client comes to see me about compulsive eating, I start by asking a simple question.</p>
<p class="blockb">Imagine you come home and go into the kitchen. A plate of warm chocolate chip cookies sits on the counter just out of the oven. Their smell hits you as you walk in. You do not feel hungry. No one else is around. What would you do?</p>
<p class="noindent">Does this question make you smile? You may think the answer is obvious, but people who are not sugar sensitive respond by saying, “I go check the phone for messages” or “I go upstairs and put on my sweats.” Some will stop and think about whether they would eat a cookie. Others will say, with no emotional charge, “Well, I might try one.” People who are not sugar sensitive do not have an emotional response to even the <span class="txit">idea</span> of smelling fresh chocolate chip cookies.</p>
</a><p class="indent"><a id="page_20">People who <span class="txit">are</span> sugar sensitive laugh at the cookie question. Their bodies are already responding to the very idea of the cookies. They </a><a id="page_21">know they would inhale a cookie—probably more than one, at that! They might eat the whole plateful, even if they were not hungry. For a sugar sensitive person, hunger is not the driving motivation. What triggers their desire to eat is the smell of the cookies, the anticipation of how the cookies will feel in their mouth, and the warmth and sweetness of the melted chocolate. Even the feeling of having a cookie in their hand will have a powerful association for them. Those cookies mean love; they mean comfort. Those cookies are friends and lovers.</a></p><a id="page_21">
<p class="indent">People who are not sugar sensitive think this response to the cookies is strange, perhaps even stupid: “What on earth are you talking about? Why would I eat a cookie if I wasn’t hungry?” But people who are sugar sensitive always know exactly what the cookie question means.</p>
<p class="indent">I have asked this question of many, many groups. Every time I’ve received dramatically consistent responses. While one part of the group will be waiting for the punch line after I ask, “Would you eat a cookie?” all the sugar sensitive people are laughing. Their bodies are already responding to the image of the plate full of warm cookies in the kitchen. Try this experiment with your friends and see what kind of response you get.</p>
<p class="indentb">Here’s a second powerful diagnostic question I use:</p>
<p class="blockb">When you were little and had Rice Krispies for breakfast, did you eat the cereal for its own sake, or did you eat the cereal so you could get to the milk and sugar at the bottom of the bowl?</p>
<p class="indent">People who are not sugar sensitive think the milk and sugar at the bottom of the bowl are disgusting. People who <span class="txit">are</span> sugar sensitive smile. They remember that the real objective was to get to the dregs of milk and sugar. They got high by tilting the cereal bowl into their mouths and tasting the clump of sugar at the bottom.</p>
<p class="indent">Your answers to these two questions may simply reinforce what you already know. Some people—perhaps including you—are very attached to sweet things.</p>
</a><p class="indent"><a id="page_21">There are lots of other ways to get clues to your sugar sensitivity. You might think back to the size of the bag you carried at Halloween. </a><a id="page_22">Children who were not sugar sensitive carried those small orange plastic pumpkins. We carried pillow cases. Their candy lasted until Easter. Ours was gone in three days.</a></p><a id="page_22">
<h3 class="h3">Diagnosing Sugar Sensitivity</h3>
<p class="noindent">If you are still asking, “How can I know for sure if I am sugar sensitive?” let’s take a look at the core issues associated with sugar sensitivity. Check each of these ten statements that applies to you:</p>
<div class="box">
<p class="bull1"><img alt="images" src="images/box.jpg"/> I really like sweet foods.</p>
<p class="bull1"><img alt="images" src="images/box.jpg"/> I eat a lot of sweets.</p>
<p class="bull1"><img alt="images" src="images/box.jpg"/> I am very fond of bread, cereal, popcorn, and/or pasta.</p>
<p class="bull1"><img alt="images" src="images/box.jpg"/> I now have or once had a problem with alcohol or drugs.</p>
<p class="bull1"><img alt="images" src="images/box.jpg"/> One or both of my parents are/were alcoholic.</p>
<p class="bull1"><img alt="images" src="images/box.jpg"/> One or both of my parents are/were especially fond of sugar.</p>
<p class="bull1"><img alt="images" src="images/box.jpg"/> I am overweight and can’t lose the extra pounds easily.</p>
<p class="bull1"><img alt="images" src="images/box.jpg"/> I continue to be depressed no matter what I do.</p>
<p class="bull1"><img alt="images" src="images/box.jpg"/> I often find myself overreacting to stress.</p>
<p class="bull1"><img alt="images" src="images/box.jpg"/> I have a history of anger that sometimes surprises even me.</p>
</div>
<p class="indent">How many boxes did you check? If you checked three or more, you are reading the right book. If you checked all ten, you are in good company. Each of these statements relates to an aspect of sugar sensitivity. Let’s go through them one at a time so you can see what your answers may have to tell you.</p>
<h5 class="h5"><span class="txbf">I really like sweet foods.</span></h5>
</a><p class="noindent"><a id="page_22">Answering yes to this question alone indicates sugar sensitivity. If you <span class="txit">really</span> like sweet foods, you may have an intense physiological response to them. Sugar sensitive people actually respond to tasting and eating sugar in a way that is more pronounced than other people. A “normal” </a><a id="page_23">person will enjoy sweets but can eat half a cookie and leave the rest for tomorrow. A “normal” person doesn’t sit through dinner thinking about dessert. A “normal” person does not feel more confident and powerful after eating sweet things.</a></p><a id="page_23">
<h5 class="h5"><span class="txbf">I eat a lot of sweets.</span></h5>
<p class="noindent">Sugar sensitive people are likely to eat a lot of sweets. Even though you feel you shouldn’t, you may eat candy, cookies, or ice cream. Dessert may be the most important part of your meal. You may fast from sweet things during the day and then binge at night.</p>
<p class="indent">Or you may really love sweet things but choose not to eat them. Lindsay, a tall, slender client of mine, was sugar sensitive. Because she was concerned about calories and fats in her diet, she had stopped eating hot fudge sundaes, candy bars, and the other sweets she usually craved. But even though she had eliminated obvious sugars from her diet, sweet things continued to find their way into her mouth. She ate energy bars for breakfast. She stopped drinking Coke and switched to fruit juice. She also discovered she loved carrot juice. She would have a glass of wine with dinner as a treat to make up for how much she missed her high-calorie splurges.</p>
</a><p class="indent"><a id="page_23">All of these foods contain high amounts of sugar. Lindsay’s sugar sensitive biochemistry was craving sugars and drove her to eat them even without her knowing what she was doing. Her energy bars and many of the other “healthy” low-fat foods she ate were very high in what their labels called “carbohydrates.” Sugars come in many forms. People who are sugar sensitive find them. Go to </a><span href="#/xhtml/ch10#page_141" title="Link target outside of Sample">page 141</span> for a fuller discussion of sugars.</p>
<h5 class="h5"><span class="txbf">I am very fond of bread, cereal, popcorn, and/or pasta.</span></h5>
<p class="noindent">Your body may respond to foods made with white flour as if they were sugars. You may find you feel good soon after eating them but then feel terrible later on. You may love bread. Cereal may be a staple for you. In the evening, you might settle on the couch with a huge bowl of popcorn.</p>
<p class="indent"><a id="page_24">Rank yourself on a scale of one to ten on your attachment to any of these foods. You may be surprised to find that even though you don’t eat “sugar,” you have a very powerful emotional attachment to bread, pasta, cereal, and/or popcorn. You would kill for French bread. You know where all the homemade pasta is sold. Don’t get nervous. It’s okay to feel this way. Your attachment to these foods only tells us how powerful your sugar sensitive biochemistry is.</a></p><a id="page_24">
<h5 class="h5"><span class="txbf">I now have or once had a problem with alcohol or drugs.</span></h5>
<p class="noindent">If you have used alcohol or drugs in an addictive way at some time in your life, it’s very likely that you have a body chemistry that responds more intensely to alcohol or drugs than other people. Your attachment to sugars sets you up biochemically for the addictive use of alcohol and even certain drugs.</p>
<p class="indent">Even if you are recovering from alcohol or drug addiction, sugar sensitivity can affect how you feel. This accounts for much of the syndrome called the dry drunk. Hair-trigger reactions and impulsive behavior can be caused by what you eat and when you eat it. Many of the unexplained physical and emotional symptoms that people take for granted in addiction recovery, such as irritability, cravings, mood swings, and sleep disturbances, actually result from having a sugar sensitive body. In addition, feelings of low self-esteem may continue long after they seem rationally warranted. For example, Christine, who got sober five years ago, expected to feel a whole lot happier and healthier than she does. She has a fabulous job, which she loves, has been promoted three times in two years, and makes $20,000 more a year than she used to. But she still worries that she will be a bag lady in her old age. The problem is that Christine stopped drinking but didn’t change her diet to compensate for her sugar sensitive body. What and when you eat can make you feel terrible or wonderful.</p>
<h5 class="h5"><span class="txbf">One or both of my parents are/were alcoholic.</span></h5>
</a><p class="noindent"><a id="page_24">If your parents drank to excess or drank in an alcoholic fashion, you may have inherited a specific type of brain-chemical response </a><a id="page_25">to alcohol that makes you feel tearful, depressed, and emotionally overwhelmed—or angry and belligerent—when you are “under the influence” of sugar. You can inherit other aspects of sugar sensitivity as well. Your parents may have been sugar sensitive long before they started to drink. Seventy-eight percent of the drunk drivers in the program I ran reported that their fathers were alcoholics and their mothers loved sweets. This combination of an alcoholic father and a sugar sensitive mother (or vice versa) maximizes the chance that you were born with a sugar sensitive body.</a></p><a id="page_25">
<h5 class="h5"><span class="txbf">One or both of my parents are/were especially fond of sugar.</span></h5>
<p class="noindent">People who are sugar sensitive often grow up in houses where sweets abound. I remember our family ritual of going to the local Dairy Queen on summer evenings. Ice cream not only created a pleasant memory, it carried a whole emotional charge as well. To this day, the memory of the sweet, cold, creamy soft treat evokes a powerful and pleasant response in my body.</p>
<p class="indent">As with the question about chocolate chip cookies, non-sugar sensitive people do not respond in this way. They may report a childhood memory of going to Dairy Queen, but it’s a memory with a different emotional content. Their bodies do not remember the feeling of the ice cream in their mouths with the same intensity. Find a non-sugar sensitive person and ask them what they remember about food from their childhood. Then ask a sugar sensitive person. I guarantee there will be a big difference in their responses.</p>
<h5 class="h5"><span class="txbf">I am overweight and can’t lose the extra pounds easily.</span></h5>
</a><p class="noindent"><a id="page_25">Sugar sensitive people often crave carbohydrates. This isn’t an emotional craving but a physiological one caused by the way their body chemistry overreacts to sweets and carbohydrates. They find dieting difficult and often unproductive in the long term. Restricting calories does not result in weight loss as it should. People who are sugar sensitive can eat as little as 800 calories a day, but if those calories are from carbohydrates, they will still gain weight. They may have tried a low-</a><a id="page_26">carb diet and initially had success. But over time it is likely that they started feeling restless and uneasy. Then they slipped and ate carbohydrates, could not get back on the diet, and experienced a disastrous rebound effect. You know the “yo-yo syndrome” well: lose 10 pounds, regain 15; lose 15, regain 20.</a></p><a id="page_26">
<h5 class="h5"><span class="txbf">I continue to be depressed no matter what I do.</span></h5>
<p class="noindent">Sugar sensitive people may have a hard time getting mobilized. You may feel frequently sad or apathetic. You may be depressed and crawl through the day with very little energy. For women, the depression may get worse just before menstruation. Often, sugar sensitive people are miserable in the winter because the decrease in daylight affects their already impaired brain chemicals. You may self-medicate your depression by eating sweet foods, since sweets are one of the few things that make you feel better, albeit temporarily. You may be taking an antidepressant like Prozac but still have symptoms of depression. If that’s the case, you likely have a sugar sensitivity aspect to your depression that neither you nor your doctor has recognized.</p>
<h5 class="h5"><span class="txbf">I often find myself overreacting to stress.</span></h5>
<p class="noindent">Volatile blood sugar levels make sugar sensitive people edgy and reactive. You may fly off the handle or cry at the drop of a hat. The conflicting feelings you have don’t seem to make sense. For example, my client Shirley worked as a senior manager in a governmental agency. She was well-thought-of, did excellent work, and liked her job. Most of the time she was steady and clear, but at other times she would get overwhelmed and want to sit and cry when her boss gave her feedback about her work. She was also surprised by the power of her anger, which seemed to bubble up from nowhere. Like Shirley, you may think of yourself as a really nice person—and most of the time you are. But at other times you feel totally out of control. These mood swings may well be due to sugar sensitivity.</p>
</a><h5 class="h5"><a id="page_26"></a><a id="page_27"><span class="txbf">I have a history of anger that sometimes surprises even me.</span></a></h5><a id="page_27">
<p class="noindentb">Sugar sensitive people can have episodes of anger that seem to overtake them without reason. You may feel like Dr. Jekyll and Mr. Hyde. Your dark side stays hidden most of the time, but those people close to you know it’s there. Your flashpoint is low and your impulse brakes don’t work. The intensity of your feelings is particularly scary because it just doesn’t seem to fit your “real” personality.</p>
<p class="indent">Are these patterns beginning to sound familiar? Does the sugar sensitive profile fit your experience? Sugar sensitive people often feel comforted by answering the questions listed above. Patterns in their lives that haven’t made sense suddenly start fitting together.</p>
<p class="indent">Sugar sensitivity is a theory—a working hypothesis—based on my own observation of how my addicted and/or compulsive clients respond to sugars; on my in-depth investigation of the solid scientific research that has been done on carbohydrate sensitivity; and on the role of brain chemicals in alcoholism, addiction, and nutrition.</p>
<p class="indent">Scientific research since the publication of this book in 1998 has now demonstrated the physiology of sugar addiction and lends strong credence to my original hypothesis of sugar sensitivity. The stories I originally heard from clients in my California practice and now from the Radiant Recovery® community all over the world tell us that my theory is on target and my program really can help you heal your sugar addiction and your biochemical imbalance. Back in the mid-1990s, naming the problem of sugar sensitivity and offering a solution was too important to wait for the approval of scientific authorities. Now the findings of researchers are catching up with my vision of twenty years ago. Since that time, hundreds of thousands of people have used the program I have created to heal their sugar sensitivity and step into the world of what we call “radiant recovery.”</p>
</a><p class="indent"><a id="page_27">Before we go any further, I want to stress that there’s nothing “bad” about having a sugar sensitive biochemistry. The more you can defuse the negative messages you have always received about your behavior, the freer you will be to begin this healing process. Remember that there are millions of people just like you—people who know </a><a id="page_28">something is wrong, who joke about being “addicted” to chocolate, but who rarely talk about what is really going on inside them.</a></p><a id="page_28">
<p class="indent">As you explore the power of your own sugar sensitivity, you may want to ask yourself some questions. Try not to be judgmental as you do this. Allow yourself a little humor about your “addiction.” Perhaps you don’t outright lie about when or whether you are eating sweets, but do you lie by omission? Do you eat sweets only when no one else is around? Do you put the bag with the goodies inside something else so people can’t see what you are carrying? Do you park your car in an out-of-the-way place to chomp on your special treat? Do you hide the candy wrappers under the other trash so your spouse won’t know what you ate? Do you eat your children’s cookies and then say you don’t know what happened to them? Do you go to the warehouse stores and buy huge quantities of candy and tell yourself that it’s a good buy? Do you know the hours of the Godiva chocolate store?</p>
<h3 class="h3">Can You Be Addicted to Sugar?</h3>
<p class="noindent">By now the answer to this question should be pretty clear to you. Yes, you can be addicted to sugar, to sweet foods, and to white-flour products that your body responds to as sugars. This addiction is physiological and affects the same biochemical systems in your body that are affected by drugs like morphine and heroin. You can actually get “high” on sugar. Eating it can make you feel euphoric immediately afterward. If you don’t have your regular sugar “fix,” you may experience withdrawal symptoms. Yes, you can become <span class="txit">physiologically</span> dependent upon the effect the sugars have on your body.</p>
<p class="indent">Being sugar sensitive means you have a special biochemistry. You have a different relationship to sugar than a person with a “normal” biochemistry. Your heart sings at the sight of a newly opened box of candy; your molecules seem to jump to attention when you get a whiff of chocolate. This sensation of your body jumping to attention is not about greed. It is the natural physiological response of a sugar sensitive person whose brain has just released a powerful chemical called beta-endorphin in response to a certain smell.</p>
</a><p class="indent"><a id="page_28"></a><a id="page_29">When you eat chocolate, is there a part of you that actually feels a greater level of self-esteem? This may seem like an outrageous idea, chocolate enhancing self-esteem. But chocolate releases beta-endorphin, and beta-endorphin causes an increase in feelings of self-esteem. Your relationship to sweet things is operating on a cellular level. It is much more powerful than you have realized.</a></p><a id="page_29">
<p class="indent">In my sugar-eating past, I never understood why I felt so much better after I had candy. I knew it was emotionally comforting, but it didn’t make sense that I felt so good after doing something so “bad.” Sometimes I would binge and start soaring with a sense of possibility about what I could do with my life. I would write plans in my journal, make lists, and feel confident that the world was all right. A few hours later I would crash and feel like nothing good would ever happen for me, no change would ever come, and I would end up a bag lady with nothing to show for my life.</p>
<p class="indent">It felt crazy. How could I possibly feel such contradictory things—and feel them almost from one minute to the next? I remember the day I sat in the library, working on my PhD, and first read about the impact of beta-endorphin on self-esteem. The hair on the back of my neck stood up. I suddenly saw the connection. I was eating chocolate as self-medication to achieve self-confidence. Instead of feeling totally stupid about my behavior, I began to see that there was wisdom in it. Consciously I wanted to feel better and more secure, and unconsciously I knew there was a relationship between chocolate and self-confidence. Of course, I turned to chocolate when I felt down.</p>
<p class="indent">The problem is, sugar-induced self-esteem doesn’t last very long. And having your self-esteem wear off that quickly is a pretty fragile way to live. The good news is, you can evoke beta-endorphin-linked self-esteem without the negative and addictive effects of chocolate. You do not need chocolate! You need self-esteem based on an inner sense of well-being that comes from biochemical balance, clarity, and feeling healthy. What you eat can have a huge effect on how you feel. I will show you how to develop a food plan that can help you overcome the drawbacks of the sugar sensitive body you inherited.</p>
</a><p class="indent"><a id="page_29">You can make sense of what is happening in your body. Learning </a><a id="page_30">about sugar sensitivity will give you a perspective that takes away the negative charge you have carried all these years about your eating. You will shift from thinking you have character defects to understanding that you have a body with a volatile blood sugar response and low levels of beta-endorphin and serotonin. You will understand that your body and brain have a heightened reaction to sweet foods and that you really can have such a thing as a sugar addiction. You will learn that many of the behaviors that you hate about yourself are simply part of the sugar sensitivity picture. And you will learn how to change all these things by what and when you eat.</a></p><a id="page_30">
<p class="indent">The good news about biochemically based behavior is that it <span class="txit">can</span> change and it can change <span class="txit">fast</span>. You do not have to pursue years of psychotherapy to get results. You can start making sense of behavior today by learning which foods affect you negatively and why. You can start changing what you eat and feel better right now. You can discover what thousands and thousands have learned and reported to me about the relationship between doing the food plan and the profound changes they have experienced in their lives.</p>
<p class="indent">I’ll let some of the people in the program tell you in their own words. Below is an actual transcript from one of our online chats. The women I was chatting with are from the U.K., Denmark, and Sweden. They have been doing the program from six months to five years.</p>
<table class="table">
<tbody><tr>
<td class="line30"><p class="tab1b"><span class="font1"><span class="txbf">Kathleen</span></span></p></td>
<td class="line70"><p class="tabb">I would like to talk about how it was “before” and how things have changed. How were they before step 1?</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Anna Marie</span></span></p></td>
<td class="line70"><p class="tab">angry, violent, wild, outta control, BE [beta-endorphin] spiking cycles all over the place</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Marti</span></span></p></td>
<td class="line70"><p class="tab">heartburn</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Shelley</span></span></p></td>
<td class="line70"><p class="tab">I felt crazy</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Chris</span></span></p></td>
<td class="line70"><p class="tab">totally bonkers</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Shelley</span></span></p></td>
<td class="line70"><p class="tab">anxiety, depression, bingeing</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Diane</span></span></p></td>
<td class="line70"><p class="tab">BS [blood sugar] crashes and some of them I didn’t realize</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ivy</span></span></p></td>
<td class="line70"><p class="tab">I am way too close to the “before” so think I will just listen :-)</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Anna Marie</span></span></p></td>
<td class="line70"><p class="tab">depressed, suicidal, angst</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><a id="page_31"><span class="font1"><span class="txbf">Heidi</span></span></a></p></td>
<td class="line70"><p class="tab">oh geez, before step 1? I was mental. tantrums, meltdowns, depression, hopelessness</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Lulu</span></span></p></td>
<td class="line70"><p class="tab">irritable</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Nancy</span></span></p></td>
<td class="line70"><p class="tab">mood swings, exhausted, overwhelmed, lonely</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Anna Marie</span></span></p></td>
<td class="line70"><p class="tab">inappropriate boundaries and sharing</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Suzanne</span></span></p></td>
<td class="line70"><p class="tab">done to</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Chris</span></span></p></td>
<td class="line70"><p class="tab">moody, angry, all alone</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Diane</span></span></p></td>
<td class="line70"><p class="tab">I too was crazy, depressed, high, bingeing</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Heidi</span></span></p></td>
<td class="line70"><p class="tab">angry, mean, nasty, moody, tired, bored, overwhelmed</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ashley</span></span></p></td>
<td class="line70"><p class="tab">crazy, madwoman, depression, hopeless, overwhelmed out of control—I once smashed up the kitchen</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Suzanne</span></span></p></td>
<td class="line70"><p class="tab">no boundaries at all</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Josie</span></span></p></td>
<td class="line70"><p class="tab">not understanding and feeling so guilty</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Chris</span></span></p></td>
<td class="line70"><p class="tab">totally helpless</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Mary</span></span></p></td>
<td class="line70"><p class="tab">rollercoaster</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Diane</span></span></p></td>
<td class="line70"><p class="tab">oh, yes, afterwards, lots of guilt tripping</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Marti</span></span></p></td>
<td class="line70"><p class="tab">totally codependent but not knowing about the concept</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Anna Marie</span></span></p></td>
<td class="line70"><p class="tab">Wondering if Mr. Hyde was taking over . . .</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Josie</span></span></p></td>
<td class="line70"><p class="tab">Trying so hard to get on top of things</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Shelley</span></span></p></td>
<td class="line70"><p class="tab">gaining weight very fast</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Suzanne</span></span></p></td>
<td class="line70"><p class="tab">soooo tired—wiped out at night</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Anna Marie</span></span></p></td>
<td class="line70"><p class="tab">couldn’t focus</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Chris</span></span></p></td>
<td class="line70"><p class="tab">yeah, extremely sleepy</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Shelley</span></span></p></td>
<td class="line70"><p class="tab">yes, no energy—too tired to cook :)</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Diane</span></span></p></td>
<td class="line70"><p class="tab">always trying to “catch up” and “get organized” (actually still not totally there—but feeling ok baby stepping my way there now ; )</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Chris</span></span></p></td>
<td class="line70"><p class="tab">oh yes!</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1b"><span class="font1"><span class="txbf">Suzanne</span></span></p></td>
<td class="line70"><p class="tabb">bingeing on sweets</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1b"><span class="font1"><span class="txbf">Kathleen</span></span></p></td>
<td class="line70"><p class="tabb">all of the above?</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Nancy</span></span></p></td>
<td class="line70"><p class="tab">yep—all of the above!</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Mannie</span></span></p></td>
<td class="line70"><p class="tab">right, Kathleen</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Heidi</span></span></p></td>
<td class="line70"><p class="tab">zero concentration</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><a id="page_32"><span class="font1"><span class="txbf">Ashley</span></span></a></p></td>
<td class="line70"><p class="tab">too muddled to cook</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ivy</span></span></p></td>
<td class="line70"><p class="tab">all of the above plus total exhaustion and despair</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Heidi</span></span></p></td>
<td class="line70"><p class="tab">muddled is a good word, Ashley</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Anna Marie</span></span></p></td>
<td class="line70"><p class="tab">foggy thick brain</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Shelley</span></span></p></td>
<td class="line70"><p class="tab">yes, Heidi</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Mary</span></span></p></td>
<td class="line70"><p class="tab">never felt like doing anything</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Mannie</span></span></p></td>
<td class="line70"><p class="tab">as long as we’ve said whacko . . . I’m in</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Diane</span></span></p></td>
<td class="line70"><p class="tab">oh, I hate that foggy thick brain : (</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1b"><span class="font1"><span class="txbf">Suzanne</span></span></p></td>
<td class="line70"><p class="tabb">Yes Shelley, and never had any food in the house or any plans. “What do you mean body—are you hungry again???!”</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1b"><span class="font1"><span class="txbf">Kathleen</span></span></p></td>
<td class="line70"><p class="tabb">and trying to act normal?</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Chris</span></span></p></td>
<td class="line70"><p class="tab">trying to pretend to be normal</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Lulu</span></span></p></td>
<td class="line70"><p class="tab">or even thinking I was normal : )</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Nancy</span></span></p></td>
<td class="line70"><p class="tab">Always pretending things are normal</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ashley</span></span></p></td>
<td class="line70"><p class="tab">oh to “the world” I was a high achiever, confident, and capable</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Shelley</span></span></p></td>
<td class="line70"><p class="tab">yes, trying to put on a brave face</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Heidi</span></span></p></td>
<td class="line70"><p class="tab">doing a pretty good job on acting normal but then bingeing and melting down at night</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Diane</span></span></p></td>
<td class="line70"><p class="tab">wondering why everyone else seemed to be so “together” and why I couldn’t seem to do that at all</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ivy</span></span></p></td>
<td class="line70"><p class="tab">I think I thought that WAS normal, and everyone else just dealt with it better.</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Anna Marie</span></span></p></td>
<td class="line70"><p class="tab">oh yes, showing an “all together” face to everyone</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Josie</span></span></p></td>
<td class="line70"><p class="tab">all or nothing attitude—“tomorrow I’ll be different”</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1b"><span class="font1"><span class="txbf">Ivy</span></span></p></td>
<td class="line70"><p class="tabb">oh yes, Josie!</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1b"><span class="font1"><span class="txbf">Kathleen</span></span></p></td>
<td class="line70"><p class="tabb">and shame?</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Anna Marie</span></span></p></td>
<td class="line70"><p class="tab">oh yeah waiting for that magic something to make it all better</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Diane</span></span></p></td>
<td class="line70"><p class="tab">yes, lots of all or nothing—still working on that one . . .</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Nancy</span></span></p></td>
<td class="line70"><p class="tab">right, Josie—tomorrow, next week, next month, I’ll . . .</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><a id="page_33"><span class="font1"><span class="txbf">Marti</span></span></a></p></td>
<td class="line70"><p class="tab">still got plenty of that!</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Suzanne</span></span></p></td>
<td class="line70"><p class="tab">yes, tomorrow I will start my new life! (yet again!)</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Mannie</span></span></p></td>
<td class="line70"><p class="tab">for not having it together . . .Yes</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ashley</span></span></p></td>
<td class="line70"><p class="tab">shame I couldn’t control the temper or the drinking</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ivy</span></span></p></td>
<td class="line70"><p class="tab">that word gives makes my hairs stand up—so much shame</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Chris</span></span></p></td>
<td class="line70"><p class="tab">bucket loads of the stuff</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Shelley</span></span></p></td>
<td class="line70"><p class="tab">big shame</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Josie</span></span></p></td>
<td class="line70"><p class="tab">and then tomorrow I didn’t do it, and then the guilt and feeling so weak and hopeless</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Diane</span></span></p></td>
<td class="line70"><p class="tab">yes, shame, but I always thought of it as guilt—what’s the dif?</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Anna Marie</span></span></p></td>
<td class="line70"><p class="tab">shame that I was such a mess and couldn’t let it on to anyone or get any help</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1b"><span class="font1"><span class="txbf">Suzanne</span></span></p></td>
<td class="line70"><p class="tabb">loads and loads of shame—being diabetic and not being able to quit</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1b"><span class="font1"><span class="txbf">Kathleen</span></span></p></td>
<td class="line70"><p class="tabb">ok, talk about how it is now</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Heidi</span></span></p></td>
<td class="line70"><p class="tab">calm</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Anna Marie</span></span></p></td>
<td class="line70"><p class="tab">big</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Heidi</span></span></p></td>
<td class="line70"><p class="tab">peaceful</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Chris</span></span></p></td>
<td class="line70"><p class="tab">happy</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Shelley</span></span></p></td>
<td class="line70"><p class="tab">calm</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Anna Marie</span></span></p></td>
<td class="line70"><p class="tab">facing the past</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1b"><span class="font1"><span class="txbf">Mary</span></span></p></td>
<td class="line70"><p class="tabb">much more stable</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1b"><span class="font1"><span class="txbf">Kathleen</span></span></p></td>
<td class="line70"><p class="tabb">think about the other side</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Chris</span></span></p></td>
<td class="line70"><p class="tab">content</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Nancy</span></span></p></td>
<td class="line70"><p class="tab">content</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Diane</span></span></p></td>
<td class="line70"><p class="tab">much more clear, patient with myself most of the time</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Penny</span></span></p></td>
<td class="line70"><p class="tab">steady and happy, understanding</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ashley</span></span></p></td>
<td class="line70"><p class="tab">calm, steady</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Shelley</span></span></p></td>
<td class="line70"><p class="tab">capable</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ivy</span></span></p></td>
<td class="line70"><p class="tab">Utter, blissful relief</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><a id="page_34"><span class="font1"><span class="txbf">Anna Marie</span></span></a></p></td>
<td class="line70"><p class="tab">seeing patterns</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Heidi</span></span></p></td>
<td class="line70"><p class="tab">patient</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ashley</span></span></p></td>
<td class="line70"><p class="tab">clear</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Suzanne</span></span></p></td>
<td class="line70"><p class="tab">balance</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Chris</span></span></p></td>
<td class="line70"><p class="tab">bright-eyed and bushy-tailed</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Suzanne</span></span></p></td>
<td class="line70"><p class="tab">patience</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Diane</span></span></p></td>
<td class="line70"><p class="tab">wanting to work things out and learn</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Heidi</span></span></p></td>
<td class="line70"><p class="tab">contented is my best word. just happy being who I am, doing what I do</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Nancy</span></span></p></td>
<td class="line70"><p class="tab">focused</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ashley</span></span></p></td>
<td class="line70"><p class="tab">joyful</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ivy</span></span></p></td>
<td class="line70"><p class="tab">full</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Mary</span></span></p></td>
<td class="line70"><p class="tab">more like rolling hills</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Marti</span></span></p></td>
<td class="line70"><p class="tab">purposeful—more measured</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ashley</span></span></p></td>
<td class="line70"><p class="tab">happy</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ivy</span></span></p></td>
<td class="line70"><p class="tab">controlled</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Mary</span></span></p></td>
<td class="line70"><p class="tab">than rollercoaster</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Nancy</span></span></p></td>
<td class="line70"><p class="tab">making progress.</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Chris</span></span></p></td>
<td class="line70"><p class="tab">excited (in a good way)</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Suzanne</span></span></p></td>
<td class="line70"><p class="tab">I really like that expression, Chris! :)</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Marti</span></span></p></td>
<td class="line70"><p class="tab">seeing the bigger picture</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Heidi</span></span></p></td>
<td class="line70"><p class="tab">capable</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ashley</span></span></p></td>
<td class="line70"><p class="tab">not afraid but accepting what will come</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Penny</span></span></p></td>
<td class="line70"><p class="tab">happy, even</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Diane</span></span></p></td>
<td class="line70"><p class="tab">yes, Nancy, making progress—nice!</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ivy</span></span></p></td>
<td class="line70"><p class="tab">trusting</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ivy</span></span></p></td>
<td class="line70"><p class="tab">peace</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Marti</span></span></p></td>
<td class="line70"><p class="tab">less reactive</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Heidi</span></span></p></td>
<td class="line70"><p class="tab">“even” is a good one, Penny!</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Josie</span></span></p></td>
<td class="line70"><p class="tab">I still do and feel a lot of the same things, but I perceive myself differently</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Nancy</span></span></p></td>
<td class="line70"><p class="tab">connected</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Shelley</span></span></p></td>
<td class="line70"><p class="tab">committed</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Penny</span></span></p></td>
<td class="line70"><p class="tab">knowledgeable, really, I’m not a mystery to me</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Mary</span></span></p></td>
<td class="line70"><p class="tab">boundaries</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><a id="page_35"><span class="font1"><span class="txbf">Diane</span></span></a></p></td>
<td class="line70"><p class="tab">when I am feeling “off” I can look at it more from a distance and try to figure out why I am so off</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1b"><span class="font1"><span class="txbf">Josie</span></span></p></td>
<td class="line70"><p class="tabb">have hope</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1b"><span class="font1"><span class="txbf">Kathleen</span></span></p></td>
<td class="line70"><p class="tabb">when you were back there, could you have imagined this?</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Shelley</span></span></p></td>
<td class="line70"><p class="tab">not in the least</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Chris</span></span></p></td>
<td class="line70"><p class="tab">no way!</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Suzanne</span></span></p></td>
<td class="line70"><p class="tab">no, I could not imagine this</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Nancy</span></span></p></td>
<td class="line70"><p class="tab">nope</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ashley</span></span></p></td>
<td class="line70"><p class="tab">no. I only kept going because I had nothing left to try</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Anna Marie</span></span></p></td>
<td class="line70"><p class="tab">not for myself, no</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Marti</span></span></p></td>
<td class="line70"><p class="tab">I find it hard to accept I have “really” changed, like I will put a hex on it all</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Chris</span></span></p></td>
<td class="line70"><p class="tab">and even if you’d told me I wouldn’t have believed you :)</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Ivy</span></span></p></td>
<td class="line70"><p class="tab">the speed of the change astounds me. guess the molecules latched on as fast as they could!</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Suzanne</span></span></p></td>
<td class="line70"><p class="tab">I didn’t know I had it in me</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Diane</span></span></p></td>
<td class="line70"><p class="tab">no way, kd, not imagine—didn’t even know what it was!</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Penny</span></span></p></td>
<td class="line70"><p class="tab">had no idea. thought it was how I was</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Chris</span></span></p></td>
<td class="line70"><p class="tab">I know what you mean, Linda</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Mary</span></span></p></td>
<td class="line70"><p class="tab">no, I knew I was “just that way”</p></td>
</tr>
<tr>
<td class="line30"><p class="tab1"><span class="font1"><span class="txbf">Nancy</span></span></p></td>
<td class="line70"><p class="tab">I believed I could not have the mood swings and some of that, but I never dreamed I’d find content[ment], peace, focus.</p></td>
</tr>
</tbody></table>
<p class="noindent">These are real people, just like you. They have not done therapy or spent money. Many of them have never met one another. But doing the food has changed their lives. As soon as you experience the truth of this for yourself, what has been an overwhelming—and perhaps shameful—mystery in your life will become a fascinating journey of self-exploration.</p>
</a>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// ch02.vue?a5c643b4